import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { BrowserRouter } from "react-router-dom";
import type { AxiosResponse } from "axios";
import type { RootState } from "../state/store";
import { jwtSelector, currentUserSelector } from "../state/selectors/authSelector";
import Router from "./Router";
import Translator from "./Translator";
import { useEffect, useState } from "react";
import {
  setAuthToken,
  isTokenExpired,
  setAxiosResponseHandler,
  setAxiosRequestHandler,
} from "../util/auth";
import { getAuthFromLocalStorage } from "../util/localStorage";
import type { IUser, IOrganization } from "src/model/model";

import { getAllProjects } from "../../features/projects/state/projectsSlice";
import { setProfile } from "../../features/profile/state/profileSlice";
import { logoutAuth, setAuth } from "../../features/auth/state/authSlice";
import { clearAlerts, setAlert } from "../../features/alert/state/alertsSlice";
import { fetchBuilds } from "../../features/builds/state/buildsSlice";
import { prettifyErrorMessage } from "../util/prettifyErrorMessage";
import { fetchProjectGroupsCompletion } from "../../features/enduserStats/state/enduserStatsSlice";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "../../features/projectGroups/components/ProjectGroupList";
import apiUrls from "../../api";
import { BUILDS_COMPONENT_ID } from "src/features/stats/components/StatsOverview/StatsOverview";
import { setActiveGptConversation } from "src/features/chatbot/state/chatbotSlice";
import { internetConnexionMonitor } from "src/features/internetConnexionMonitor/internetConnexionMonitor";
import { useTranslation } from "react-i18next";
import { scheduleLogoutWhenTokenExpires } from "../util/scheduleLogoutWhenTokenExpires";
import { fetchLanguages } from "src/features/languages/state/languagesSlice";
import { MODERN_LANGUAGE_SELECTOR_COMPONENT_ID } from "src/pages/Projects/LanguageSelection/ModernLanguageSelector";

export const productionMode =
  process.env.NODE_ENV !== "development" && !process.env.REACT_APP_DEBUG;

export default function App() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Add a state to track Axios readiness
  const [axiosReady, setAxiosReady] = useState(false);

  const { jwt, user } = useAppSelector((state: RootState) => {
    return {
      jwt: jwtSelector(state),
      user: currentUserSelector(state),
    };
  });

  useEffect(() => {
    if (jwt && !isTokenExpired(jwt)) {
      dispatch(clearAlerts());
      setAuthToken(jwt);

      // Initial dispatch of user & organization
      const currentUser = JSON.parse(localStorage.getItem("wixar.profile.user") as string) as IUser;
      const organization = JSON.parse(
        localStorage.getItem("wixar.profile.organization") as string,
      ) as IOrganization;
      dispatch(setProfile({ currentUser, organization }));
      if (currentUser?.active_gpt_conversation) {
        dispatch(setActiveGptConversation(currentUser.active_gpt_conversation));
      }
      setAxiosReady(true); // Indicate that Axios is ready to make requests
    } else {
      setAxiosReady(false); // Ensure Axios is not ready if the token isn't valid
    }

    // Disconnect stale sessions cf. WIXAR-4139
    const testMode = 0;
    const generateTestToken = (expiresInSeconds: number): string => {
      const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" }));
      const payload = btoa(
        JSON.stringify({ exp: Math.floor(Date.now() / 1000) + expiresInSeconds }),
      );
      const signature = "test_signature";
      return `${header}.${payload}.${signature}`;
    };

    const token = testMode ? generateTestToken(15) : jwt;
    if (token) {
      scheduleLogoutWhenTokenExpires({ jwt: token, logoutCb: () => dispatch(logoutAuth()) });
    }
  }, [jwt]);

  useEffect(() => {
    if (axiosReady) {
      void dispatch(fetchLanguages({ componentId: MODERN_LANGUAGE_SELECTOR_COMPONENT_ID }));
    }
  }, [axiosReady]);

  useEffect(() => {
    if (user) {
      if (user.role.name === "Creator") {
        void dispatch(getAllProjects({ componentId: "projectListComponent" }));
        void dispatch(fetchBuilds({ componentId: BUILDS_COMPONENT_ID }));
      }
      if (user.role.name === "Enduser") {
        void dispatch(
          fetchProjectGroupsCompletion({ limit: 3, componentId: PROJECT_GROUP_LIST_COMPONENT_ID }),
        );
      }
    }
  }, [user]);

  const authFromLocalStorage = getAuthFromLocalStorage();
  if (
    authFromLocalStorage.user &&
    authFromLocalStorage.organization &&
    authFromLocalStorage.jwt &&
    authFromLocalStorage.jwt !== null &&
    !jwt &&
    !isTokenExpired(authFromLocalStorage.jwt)
  ) {
    dispatch(
      setAuth({
        jwt: authFromLocalStorage.jwt,
        user: authFromLocalStorage.user,
        organization: authFromLocalStorage.organization,
      }),
    );
  }

  useEffect(() => {
    console.log("Setting the axios response handler...");

    // Set axios interceptor to use along Alert component
    setAxiosResponseHandler((response: AxiosResponse | undefined) => {
      // Error localization & prettification is happening in src/common/util/prettifyErrorMessage.ts

      if (response?.data) {
        const parsedAxiosError = JSON.stringify(response?.data);
        const prettyErrorMessage = prettifyErrorMessage(parsedAxiosError);

        const silentErrorUrls = [
          apiUrls.auth.checkPwd,
          apiUrls.medias.blockadeLabs.generate,
          apiUrls.medias.generateAudio,
        ];
        const responseURL = (response.request as XMLHttpRequest | undefined)?.responseURL;
        if (!silentErrorUrls.some((url) => responseURL?.includes(url))) {
          dispatch(
            setAlert({
              msg: prettyErrorMessage.msg,
              subMsg: prettyErrorMessage.subMsg,
              type: "warning",
              isOpen: true,
              customTimeoutInMs: 10000,
            }),
          );
        }
      }
    });
    setAxiosRequestHandler();
  }, []);

  // Internet monitor cf. WIXAR-4060
  useEffect(() => {
    const monitor = internetConnexionMonitor({
      showAlert: () => dispatch(setAlert({ type: "warning", msg: t("alerts.unstableConnexion") })),
      hideAlert: () => dispatch(clearAlerts()),
    });

    return () => {
      monitor.stop();
    };
  }, [dispatch]);

  useEffect(() => {
    if (productionMode) {
      console.log = function () {};

      console.warn = function () {};

      console.error = function () {};

      console.time = function () {};

      console.timeEnd = function () {};
    } else {
      document.body.className = "devmode-body";
      const originalConsoleError = console.error.bind(console);
      console.error = function (...args: unknown[]) {
        const modifiedArgs = args.map((arg) => {
          if (typeof arg === "string") {
            return arg.replace(/webpack-internal:\/\/\//g, "");
          }
          return arg;
        });
        originalConsoleError(...modifiedArgs);
      };
    }
  }, []);

  return (
    <Translator>
      <BrowserRouter>
        <Router />
      </BrowserRouter>
    </Translator>
  );
}
